import { apiFetch } from './client';
import type { AddItemUnitBatchResult } from './masters';
import type { ImportBatch } from '../units/stockImport';
import { toBatchRequest } from '../units/intake';

/**
 * Stock imported from a shop's spreadsheet — the server's half (`/api/stock-import`). The website
 * reads the workbook and plans every row (`units/stockImport.ts`); these record the import, ask
 * which rows came in before, book each batch in through the ordinary goods receipt and undo the
 * lot. No call here carries a file.
 */

export interface StockImport {
  id: number;
  fileName: string;
  sheetName: string | null;
  rowsRead: number;
  rowsImported: number;
  unitsImported: number;
  /** Designs the import created, which Undo removes with it. */
  designsCreated: number;
  createdOn: string;
  undoneOn: string | null;
  undoReason: string | null;
  /** Whether Undo would be accepted now — no garment rented, held, booked, moved or billed. */
  canUndo: boolean;
}

export interface StockImportCheck {
  /** Fingerprints of rows a live import already brought in at this branch. */
  imported: string[];
  /** Own tag codes already on a garment, anywhere in the company. */
  takenSerials: string[];
  /** Reference numbers (RN) already on a garment, cleaned (core `normalizeReference`). */
  takenReferenceNumbers?: string[];
}

export function startStockImport(input: {
  fileName: string;
  sheetName: string | null;
  rowsRead: number;
}) {
  return apiFetch<StockImport>('/api/stock-import', { method: 'POST', body: input });
}

/** Asked before the check screen, so both show as row reasons instead of a batch refusing. */
export function checkStockImport(input: {
  fingerprints: string[];
  serials: string[];
  referenceNumbers: string[];
}) {
  return apiFetch<StockImportCheck>('/api/stock-import/check', { method: 'POST', body: input });
}

/** The designs this import created (through the ordinary create), so Undo takes them back too. */
export function linkStockImportDesigns(id: number, itemIds: number[]) {
  return apiFetch<StockImport>(`/api/stock-import/${id}/designs`, {
    method: 'POST',
    body: { itemIds },
  });
}

/** One batch: the delivery the New stock page would send, with the sheet rows it came from. */
export function sendStockImportBatch(id: number, batch: ImportBatch) {
  return apiFetch<AddItemUnitBatchResult>(`/api/stock-import/${id}/batch`, {
    method: 'POST',
    body: {
      batch: toBatchRequest(batch.draft),
      rows: batch.rows.map((row) => ({ rowNumber: row.number, fingerprint: row.fingerprint })),
    },
  });
}

export function undoStockImport(id: number, reason: string) {
  return apiFetch<StockImport>(`/api/stock-import/${id}/undo`, {
    method: 'POST',
    body: { reason },
  });
}
